// ProChart — Production-grade TradingView lightweight-charts v5 component
// Real-time WebSocket tick updates, 7 technical indicators, algo signal overlays
import React, { useRef, useEffect, memo } from 'react';
import {
  createChart, createTextWatermark, createSeriesMarkers,
  ColorType, CrosshairMode, LineStyle,
  CandlestickSeries, LineSeries, HistogramSeries,
} from 'lightweight-charts';
import { CHART_COLORS, REGIME_CHART_COLORS } from '../../../config/tradingConstants';
import CandleBuilder from './candleBuilder';
import OverlayManager from './overlayManager';

// ─── Indicator Calculators ───────────────────────────────────────────

function calcSMA(data, period, field = 'close') {
  const result = [];
  for (let i = 0; i < data.length; i++) {
    if (i < period - 1) { result.push(null); continue; }
    let sum = 0;
    for (let j = i - period + 1; j <= i; j++) sum += data[j][field];
    result.push(sum / period);
  }
  return result;
}

function calcEMA(data, period, field = 'close') {
  const k = 2 / (period + 1);
  const ema = [];
  data.forEach((d, i) => {
    if (i === 0) ema.push(d[field]);
    else ema.push(d[field] * k + ema[i - 1] * (1 - k));
  });
  return ema;
}

function calcBB(data, period = 20, stdDev = 2) {
  const sma = calcSMA(data, period);
  return data.map((_, i) => {
    if (i < period - 1 || sma[i] === null) return null;
    const slice = data.slice(i - period + 1, i + 1);
    const mean = sma[i];
    const variance = slice.reduce((s, d) => s + Math.pow(d.close - mean, 2), 0) / period;
    const sd = Math.sqrt(variance);
    return { upper: mean + stdDev * sd, lower: mean - stdDev * sd, middle: mean };
  });
}

function calcVWAP(data) {
  let cumulativeTPV = 0;
  let cumulativeVol = 0;
  return data.map((d) => {
    const tp = (d.high + d.low + d.close) / 3;
    cumulativeTPV += tp * (d.volume || 0);
    cumulativeVol += (d.volume || 0);
    return cumulativeVol > 0 ? cumulativeTPV / cumulativeVol : null;
  });
}

function calcATR(data, period = 14) {
  const tr = data.map((d, i) => {
    if (i === 0) return d.high - d.low;
    const prev = data[i - 1].close;
    return Math.max(d.high - d.low, Math.abs(d.high - prev), Math.abs(d.low - prev));
  });
  const atr = [];
  tr.forEach((_, i) => {
    if (i < period - 1) { atr.push(null); return; }
    if (i === period - 1) {
      atr.push(tr.slice(0, period).reduce((s, v) => s + v, 0) / period);
    } else {
      atr.push((atr[i - 1] * (period - 1) + tr[i]) / period);
    }
  });
  return atr;
}

function calcSuperTrend(data, period = 10, multiplier = 3) {
  const atr = calcATR(data, period);
  const result = [];
  let prevUpper = 0, prevLower = 0, prevST = 0;
  data.forEach((d, i) => {
    if (i < period || atr[i] === null) { result.push(null); return; }
    const hl2 = (d.high + d.low) / 2;
    let upper = hl2 + multiplier * atr[i];
    let lower = hl2 - multiplier * atr[i];
    upper = (upper < prevUpper || data[i - 1].close > prevUpper) ? upper : prevUpper;
    lower = (lower > prevLower || data[i - 1].close < prevLower) ? lower : prevLower;
    let trend;
    if (prevST === prevUpper) trend = d.close > upper ? 1 : -1;
    else trend = d.close < lower ? -1 : 1;
    const st = trend === 1 ? lower : upper;
    result.push({ value: st, trend });
    prevUpper = upper;
    prevLower = lower;
    prevST = st;
  });
  return result;
}

function calcRSI(data, period = 14) {
  const result = [];
  let avgGain = 0, avgLoss = 0;
  for (let i = 0; i < data.length; i++) {
    if (i === 0) { result.push(null); continue; }
    const change = data[i].close - data[i - 1].close;
    const gain = change > 0 ? change : 0;
    const loss = change < 0 ? -change : 0;
    if (i <= period) {
      avgGain += gain;
      avgLoss += loss;
      if (i === period) {
        avgGain /= period;
        avgLoss /= period;
        const rs = avgLoss === 0 ? 100 : avgGain / avgLoss;
        result.push(100 - 100 / (1 + rs));
      } else {
        result.push(null);
      }
    } else {
      avgGain = (avgGain * (period - 1) + gain) / period;
      avgLoss = (avgLoss * (period - 1) + loss) / period;
      const rs = avgLoss === 0 ? 100 : avgGain / avgLoss;
      result.push(100 - 100 / (1 + rs));
    }
  }
  return result;
}

function calcMACD(data, fast = 12, slow = 26, signal = 9) {
  const emaFast = calcEMA(data, fast);
  const emaSlow = calcEMA(data, slow);
  const macdLine = emaFast.map((f, i) => (i >= slow - 1) ? f - emaSlow[i] : null);
  const validMacd = [];
  const signalLine = new Array(data.length).fill(null);
  const histogram = new Array(data.length).fill(null);
  let startIdx = -1;
  macdLine.forEach((v, i) => {
    if (v !== null) {
      validMacd.push(v);
      if (startIdx === -1) startIdx = i;
    }
  });
  if (validMacd.length >= signal) {
    const k = 2 / (signal + 1);
    let ema = validMacd.slice(0, signal).reduce((s, v) => s + v, 0) / signal;
    signalLine[startIdx + signal - 1] = ema;
    histogram[startIdx + signal - 1] = macdLine[startIdx + signal - 1] - ema;
    for (let j = signal; j < validMacd.length; j++) {
      ema = validMacd[j] * k + ema * (1 - k);
      const idx = startIdx + j;
      signalLine[idx] = ema;
      histogram[idx] = macdLine[idx] - ema;
    }
  }
  return { macdLine, signalLine, histogram };
}

// ─── Component ───────────────────────────────────────────────────────

function ProChart({
  candles,
  indicators = [],
  symbolName = '',
  timeframe = 'FIFTEEN_MINUTE',
  liveTick,
  signal,
  expectedMove,
  regime,
  orbZone,
  prevDayHL,
  weeklyHL,
  tradeMarkers,
}) {
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const seriesRef = useRef({});
  const candleBuilderRef = useRef(null);
  const overlayManagerRef = useRef(null);
  const markerPluginRef = useRef(null);

  // ── Effect 1: Create chart + candle/volume series + CandleBuilder + OverlayManager
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const chart = createChart(container, {
      layout: {
        background: { type: ColorType.Solid, color: '#FFFFFF' },
        textColor: '#6B7280',
        fontSize: 11,
        fontFamily: "'Inter', -apple-system, sans-serif",
      },
      grid: {
        vertLines: { color: '#F3F4F6', style: LineStyle.Solid },
        horzLines: { color: '#F3F4F6', style: LineStyle.Solid },
      },
      crosshair: {
        mode: CrosshairMode.Normal,
        vertLine: { color: '#9CA3AF', width: 1, style: LineStyle.Dashed, labelBackgroundColor: '#374151' },
        horzLine: { color: '#9CA3AF', width: 1, style: LineStyle.Dashed, labelBackgroundColor: '#374151' },
      },
      rightPriceScale: {
        borderColor: '#E5E7EB',
        scaleMargins: { top: 0.05, bottom: 0.25 },
      },
      timeScale: {
        borderColor: '#E5E7EB',
        timeVisible: true,
        secondsVisible: false,
        rightOffset: 5,
        barSpacing: 8,
        minBarSpacing: 3,
      },
      handleScroll: { vertTouchDrag: false },
    });

    if (symbolName) {
      createTextWatermark(chart.panes()[0], {
        horzAlign: 'center',
        vertAlign: 'center',
        lines: [{ text: symbolName, color: 'rgba(0, 0, 0, 0.04)', fontSize: 48 }],
      });
    }

    const candleSeries = chart.addSeries(CandlestickSeries, {
      upColor: '#FFFFFF',
      downColor: CHART_COLORS.bearish,
      borderUpColor: CHART_COLORS.bullish,
      borderDownColor: CHART_COLORS.bearish,
      wickUpColor: CHART_COLORS.bullish,
      wickDownColor: CHART_COLORS.bearish,
    });

    const volumeSeries = chart.addSeries(HistogramSeries, {
      priceFormat: { type: 'volume' },
      priceScaleId: 'volume',
    });
    chart.priceScale('volume').applyOptions({
      scaleMargins: { top: 0.8, bottom: 0 },
    });

    const markerPlugin = createSeriesMarkers(candleSeries, []);
    const builder = new CandleBuilder(timeframe);
    const overlay = new OverlayManager(candleSeries, markerPlugin);

    chartRef.current = chart;
    seriesRef.current = { candle: candleSeries, volume: volumeSeries };
    candleBuilderRef.current = builder;
    overlayManagerRef.current = overlay;
    markerPluginRef.current = markerPlugin;

    const resizeObserver = new ResizeObserver(entries => {
      if (entries.length === 0 || !container) return;
      const { width, height } = entries[0].contentRect;
      chart.applyOptions({ width, height });
    });
    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
      overlay.removeAll();
      chart.remove();
      chartRef.current = null;
      seriesRef.current = {};
      candleBuilderRef.current = null;
      overlayManagerRef.current = null;
      markerPluginRef.current = null;
    };
  }, [symbolName]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Effect 2: Reset CandleBuilder interval on timeframe change
  useEffect(() => {
    if (candleBuilderRef.current) {
      candleBuilderRef.current.setTimeframe(timeframe);
    }
  }, [timeframe]);

  // ── Effect 3: Load historical data into builder → setData on series
  useEffect(() => {
    const chart = chartRef.current;
    const builder = candleBuilderRef.current;
    const { candle, volume } = seriesRef.current;
    if (!chart || !builder || !candle || !volume || !candles || candles.length === 0) return;

    builder.setHistoricalCandles(candles);
    candle.setData(builder.getCandles());
    volume.setData(builder.getVolumeData());
    chart.timeScale().fitContent();
  }, [candles]);

  // ── Effect 4: Update current candle from WebSocket tick
  useEffect(() => {
    const builder = candleBuilderRef.current;
    const { candle, volume } = seriesRef.current;
    if (!builder || !candle || !volume || !liveTick) return;

    const { updated, candle: updatedCandle } = builder.processTick(liveTick);
    if (updated && updatedCandle) {
      candle.update(updatedCandle);
      volume.update({
        time: updatedCandle.time,
        value: updatedCandle.volume,
        color: updatedCandle.close >= updatedCandle.open
          ? 'rgba(22, 163, 74, 0.3)'
          : 'rgba(239, 68, 68, 0.3)',
      });
    }
  }, [liveTick]);

  // ── Effect 5: Rebuild indicator overlay series
  useEffect(() => {
    const chart = chartRef.current;
    const builder = candleBuilderRef.current;
    if (!chart || !builder) return;

    const data = builder.getCandles();
    if (!data || data.length === 0) return;

    // Remove old indicator series (keep candle + volume)
    const keep = new Set(['candle', 'volume']);
    Object.keys(seriesRef.current).forEach(key => {
      if (!keep.has(key)) {
        try { chart.removeSeries(seriesRef.current[key]); } catch (e) { /* already removed */ }
        delete seriesRef.current[key];
      }
    });

    const times = data.map(c => c.time);

    function toLineData(values) {
      const result = [];
      values.forEach((v, i) => {
        if (v !== null && v !== undefined && isFinite(v)) {
          result.push({ time: times[i], value: v });
        }
      });
      return result;
    }

    // SMA
    if (indicators.includes('sma')) {
      const sma = calcSMA(data, 20);
      const s = chart.addSeries(LineSeries, { color: CHART_COLORS.sma20, lineWidth: 1.5, priceLineVisible: false, lastValueVisible: false });
      s.setData(toLineData(sma));
      seriesRef.current.sma = s;
    }

    // EMA
    if (indicators.includes('ema')) {
      const ema = calcEMA(data, 20);
      const s = chart.addSeries(LineSeries, { color: CHART_COLORS.ema20, lineWidth: 1.5, priceLineVisible: false, lastValueVisible: false });
      s.setData(toLineData(ema));
      seriesRef.current.ema = s;
    }

    // Bollinger Bands
    if (indicators.includes('bb')) {
      const bb = calcBB(data);
      const upperData = [], lowerData = [], midData = [];
      bb.forEach((b, i) => {
        if (b) {
          const t = times[i];
          if (isFinite(b.upper)) upperData.push({ time: t, value: b.upper });
          if (isFinite(b.lower)) lowerData.push({ time: t, value: b.lower });
          if (isFinite(b.middle)) midData.push({ time: t, value: b.middle });
        }
      });
      const bbU = chart.addSeries(LineSeries, { color: CHART_COLORS.bb.upper, lineWidth: 1, lineStyle: LineStyle.Dashed, priceLineVisible: false, lastValueVisible: false });
      bbU.setData(upperData);
      seriesRef.current.bbUpper = bbU;
      const bbL = chart.addSeries(LineSeries, { color: CHART_COLORS.bb.lower, lineWidth: 1, lineStyle: LineStyle.Dashed, priceLineVisible: false, lastValueVisible: false });
      bbL.setData(lowerData);
      seriesRef.current.bbLower = bbL;
      const bbM = chart.addSeries(LineSeries, { color: CHART_COLORS.bb.upper, lineWidth: 1, priceLineVisible: false, lastValueVisible: false });
      bbM.setData(midData);
      seriesRef.current.bbMid = bbM;
    }

    // VWAP
    if (indicators.includes('vwap')) {
      const vwap = calcVWAP(data);
      const s = chart.addSeries(LineSeries, { color: CHART_COLORS.vwap, lineWidth: 2, priceLineVisible: false, lastValueVisible: false });
      s.setData(toLineData(vwap));
      seriesRef.current.vwap = s;
    }

    // SuperTrend
    if (indicators.includes('supertrend')) {
      const st = calcSuperTrend(data);
      const upData = [], downData = [];
      st.forEach((v, i) => {
        if (v && isFinite(v.value)) {
          const pt = { time: times[i], value: v.value };
          if (v.trend === 1) upData.push(pt);
          else downData.push(pt);
        }
      });
      const stUp = chart.addSeries(LineSeries, { color: CHART_COLORS.supertrend.up, lineWidth: 2, priceLineVisible: false, lastValueVisible: false });
      stUp.setData(upData);
      seriesRef.current.stUp = stUp;
      const stDown = chart.addSeries(LineSeries, { color: CHART_COLORS.supertrend.down, lineWidth: 2, priceLineVisible: false, lastValueVisible: false });
      stDown.setData(downData);
      seriesRef.current.stDown = stDown;
    }

    // RSI (sub-pane)
    if (indicators.includes('rsi')) {
      const rsi = calcRSI(data);
      const rsiS = chart.addSeries(LineSeries, { color: CHART_COLORS.rsi, lineWidth: 1.5, priceScaleId: 'rsi', priceLineVisible: false, lastValueVisible: true });
      rsiS.setData(toLineData(rsi));
      seriesRef.current.rsi = rsiS;
      chart.priceScale('rsi').applyOptions({ scaleMargins: { top: 0.8, bottom: 0.0 } });
      const rsiData = toLineData(rsi);
      if (rsiData.length >= 2) {
        const ft = rsiData[0].time, lt = rsiData[rsiData.length - 1].time;
        const r70 = chart.addSeries(LineSeries, { color: 'rgba(239, 68, 68, 0.3)', lineWidth: 1, lineStyle: LineStyle.Dashed, priceScaleId: 'rsi', priceLineVisible: false, lastValueVisible: false });
        r70.setData([{ time: ft, value: 70 }, { time: lt, value: 70 }]);
        seriesRef.current.rsi70 = r70;
        const r30 = chart.addSeries(LineSeries, { color: 'rgba(22, 163, 74, 0.3)', lineWidth: 1, lineStyle: LineStyle.Dashed, priceScaleId: 'rsi', priceLineVisible: false, lastValueVisible: false });
        r30.setData([{ time: ft, value: 30 }, { time: lt, value: 30 }]);
        seriesRef.current.rsi30 = r30;
      }
    }

    // MACD (sub-pane)
    if (indicators.includes('macd')) {
      const { macdLine, signalLine, histogram } = calcMACD(data);
      const histData = [];
      histogram.forEach((v, i) => {
        if (v !== null && isFinite(v)) {
          histData.push({ time: times[i], value: v, color: v >= 0 ? 'rgba(22, 163, 74, 0.4)' : 'rgba(239, 68, 68, 0.4)' });
        }
      });
      const mH = chart.addSeries(HistogramSeries, { priceScaleId: 'macd', priceLineVisible: false, lastValueVisible: false });
      mH.setData(histData);
      seriesRef.current.macdHist = mH;
      chart.priceScale('macd').applyOptions({ scaleMargins: { top: 0.85, bottom: 0.0 } });
      const mL = chart.addSeries(LineSeries, { color: CHART_COLORS.macd, lineWidth: 1.5, priceScaleId: 'macd', priceLineVisible: false, lastValueVisible: false });
      mL.setData(toLineData(macdLine));
      seriesRef.current.macdLine = mL;
      const mS = chart.addSeries(LineSeries, { color: CHART_COLORS.macdSignal, lineWidth: 1, priceScaleId: 'macd', priceLineVisible: false, lastValueVisible: false });
      mS.setData(toLineData(signalLine));
      seriesRef.current.macdSignal = mS;
    }

    // Adjust margins based on active sub-panes
    const hasRSI = indicators.includes('rsi');
    const hasMACD = indicators.includes('macd');
    let bottomMargin = 0.25;
    if (hasRSI && hasMACD) bottomMargin = 0.45;
    else if (hasRSI || hasMACD) bottomMargin = 0.35;

    chart.priceScale('right').applyOptions({ scaleMargins: { top: 0.05, bottom: bottomMargin } });

    if (hasRSI) {
      chart.priceScale('rsi').applyOptions({
        scaleMargins: { top: hasMACD ? 0.65 : 0.75, bottom: hasMACD ? 0.2 : 0.0 },
      });
    }
    if (hasMACD) {
      chart.priceScale('macd').applyOptions({
        scaleMargins: { top: hasRSI ? 0.82 : 0.75, bottom: 0.0 },
      });
    }
    chart.priceScale('volume').applyOptions({
      scaleMargins: { top: bottomMargin - 0.03, bottom: 1 - bottomMargin + 0.02 },
    });
  }, [candles, indicators]);

  // ── Effect 6: Entry/SL/Target lines + entry marker from algo signal
  useEffect(() => {
    const overlay = overlayManagerRef.current;
    if (!overlay) return;
    if (!signal) {
      overlay.removeSignalLines();
      return;
    }
    const builder = candleBuilderRef.current;
    const latest = builder?.getLatestCandle();
    overlay.applySignal(signal, latest?.time);
  }, [signal]);

  // ── Effect 7: Expected move bands
  useEffect(() => {
    const overlay = overlayManagerRef.current;
    if (!overlay) return;
    if (!expectedMove || !expectedMove.upper || !expectedMove.lower) {
      overlay.removeExpectedMoveBands();
      return;
    }
    overlay.setExpectedMoveBands(expectedMove.upper, expectedMove.lower);
  }, [expectedMove]);

  // ── Effect 8: Level overlays (ORB, prevDayHL, weeklyHL)
  useEffect(() => {
    const overlay = overlayManagerRef.current;
    if (!overlay) return;
    if (orbZone?.high && orbZone?.low) overlay.setORBZone(orbZone.high, orbZone.low);
    else overlay.removeORBZone();
    if (prevDayHL?.high && prevDayHL?.low) overlay.setPrevDayHighLow(prevDayHL.high, prevDayHL.low);
    else overlay.removePrevDayHighLow();
    if (weeklyHL?.high && weeklyHL?.low) overlay.setWeeklyHighLow(weeklyHL.high, weeklyHL.low);
    else overlay.removeWeeklyHighLow();
  }, [orbZone, prevDayHL, weeklyHL]);

  // ── Effect 9: Trade history markers
  useEffect(() => {
    const overlay = overlayManagerRef.current;
    if (!overlay) return;
    if (tradeMarkers && tradeMarkers.length > 0) {
      overlay.setTradeMarkers(tradeMarkers);
    } else {
      overlay.clearMarkers();
    }
  }, [tradeMarkers]);

  // ── Regime badge color
  const regimeColor = regime?.regime ? REGIME_CHART_COLORS[regime.regime] || '#6B7280' : null;

  return (
    <div className="relative w-full h-full">
      <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
      {regime?.regime && (
        <div
          className="absolute top-2 right-2 z-10 pointer-events-none px-2 py-1 rounded-md text-[10px] font-bold text-white"
          style={{ backgroundColor: regimeColor }}
        >
          {regime.regime.replace(/_/g, ' ')}
        </div>
      )}
    </div>
  );
}

export default memo(ProChart);
